import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Instagram, Facebook, Music2, MessageCircle, CheckCircle2, Loader2 } from "lucide-react";

const initial = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("All fields are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contacts", form);
      setSuccess(true);
      setForm(initial);
      toast.success("Message sent!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grain section-pad" data-testid="contact-page">
      <div className="container-vg">
        <div className="overline">Get In Touch</div>
        <h1 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tighter mt-4 max-w-4xl">
          Storms, science, <br /><span className="text-yellow-400">say hello.</span>
        </h1>
        <p className="text-zinc-400 mt-6 max-w-2xl text-lg">
          Media inquiries, research collaborations, partnerships, or just a question about a storm we chased — reach out.
          We read every message.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-16">
          {/* Contact info */}
          <aside className="lg:col-span-4 space-y-8">
            <div className="card-vg p-7">
              <Mail className="w-6 h-6 text-yellow-400" />
              <div className="overline mt-4">Email</div>
              <a href="mailto:vortexspotter@vortexghost.business" className="font-mono text-white mt-2 text-sm block hover:text-yellow-400 break-all" data-testid="contact-email-link">vortexspotter@vortexghost.business</a>
            </div>

            <div className="card-vg p-7">
              <Phone className="w-6 h-6 text-yellow-400" />
              <div className="overline mt-4">Field Hotline</div>
              <a href="tel:+12564043789" className="font-mono text-white mt-2 text-sm block hover:text-yellow-400" data-testid="contact-phone-link">+1 (256) 404-3789</a>
              <div className="text-zinc-500 text-xs mt-1">Active during chase days</div>
            </div>

            <div className="card-vg p-7">
              <MapPin className="w-6 h-6 text-yellow-400" />
              <div className="overline mt-4">Operations Coverage</div>
              <div className="font-mono text-white mt-2 text-sm leading-relaxed">
                All 50 States<br />
                <span className="text-zinc-500">United States</span>
              </div>
            </div>

            <div className="card-vg p-7">
              <div className="overline">Follow Field Ops</div>
              <div className="flex items-center gap-3 mt-5">
                <a href="https://instagram.com/vortexghosttt" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/10 hover:border-yellow-400 hover:text-yellow-400 flex items-center justify-center" data-testid="contact-social-instagram" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
                <a href="#" className="w-10 h-10 border border-white/10 hover:border-yellow-400 hover:text-yellow-400 flex items-center justify-center" data-testid="contact-social-facebook" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
                <a href="#" className="w-10 h-10 border border-white/10 hover:border-yellow-400 hover:text-yellow-400 flex items-center justify-center" data-testid="contact-social-tiktok" aria-label="TikTok"><Music2 className="w-4 h-4" /></a>
                <a href="#" className="w-10 h-10 border border-white/10 hover:border-yellow-400 hover:text-yellow-400 flex items-center justify-center" data-testid="contact-social-discord" aria-label="Discord"><MessageCircle className="w-4 h-4" /></a>
              </div>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8">
            {success ? (
              <div className="card-vg p-12 text-center" data-testid="contact-success">
                <div className="w-16 h-16 mx-auto bg-green-500/10 border border-green-500/40 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
                <h2 className="font-heading text-3xl font-bold mt-6">Message received.</h2>
                <p className="text-zinc-400 mt-3">We'll get back to you within 24–48 hours.</p>
                <button onClick={() => setSuccess(false)} className="btn-secondary mt-8" data-testid="contact-send-another">
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="card-vg p-8 md:p-10 space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="overline mb-2 block !text-zinc-400">Your Name *</label>
                    <input value={form.name} onChange={(e) => update("name", e.target.value)} className="input-vg" data-testid="contact-field-name" />
                  </div>
                  <div>
                    <label className="overline mb-2 block !text-zinc-400">Email *</label>
                    <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="input-vg" data-testid="contact-field-email" />
                  </div>
                </div>
                <div>
                  <label className="overline mb-2 block !text-zinc-400">Subject *</label>
                  <input value={form.subject} onChange={(e) => update("subject", e.target.value)} className="input-vg" placeholder="What's this about?" data-testid="contact-field-subject" />
                </div>
                <div>
                  <label className="overline mb-2 block !text-zinc-400">Message *</label>
                  <textarea rows={7} value={form.message} onChange={(e) => update("message", e.target.value)} className="input-vg resize-none" placeholder="Tell us what's on your mind…" data-testid="contact-field-message" />
                </div>
                <div className="pt-2 flex justify-end">
                  <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50" data-testid="contact-submit-btn">
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send Message <Send className="w-4 h-4" /></>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
